import React, { useCallback, useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table } from 'antd'
import BaseListing from 'src/BaseListings'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import './style.css'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'

const ItemSerialReport = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [ItemSerialReportData, setItemSerialReportData] = useState([])
  const [Items, setItems] = useState([])
  const [ItemId, setItemId] = useState('')
  const [ItemName, setItemName] = useState('')
  const [dateRange, setDateRange] = useState([])

  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [perPage] = useState(200)

  const Base = new BaseListing('sale_order_report')

  useEffect(() => {
    Base.dropdownData('items', {}, [setItems], 1)
  }, [])

  const changeValues = (key, val) => {
    if (key === 'date_range') setDateRange(val)
    if (key === 'item_id') {
      setItemId(val.value)
      setItemName(val.label)
    }
  }

  const exportToExcel = () => {
    setIsLoading(true)
    wtApiPostWithContentTypeHandling('item_serial_report/export', {
      from_date: dateRange[0].format('YYYY-MM-DD'),
      to_date: dateRange[1].format('YYYY-MM-DD'),
      item_id: ItemId,
    }).finally(() => setIsLoading(false))
  }

  const loadReportData = (loadMore = false) => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    setIsLoading(true)

    wtApiCall(
      'reports/item_serial_report',
      {
        from_date: dateRange[0].format('YYYY-MM-DD'),
        to_date: dateRange[1].format('YYYY-MM-DD'),
        item_id: ItemId,
        page: page,
        per_page: perPage,
      },
      'get',
      (response) => {
        const res = response.data.data || []
        if (loadMore) {
          setItemSerialReportData((prev) => [...prev, ...res])
        } else {
          setItemSerialReportData(res)
        }

        setHasMore(res.length)
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const handleSearch = () => {
    setPage(1)
    setHasMore(true)
    loadReportData(false)
  }

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 300; // buffer

    if (isNearBottom && hasMore && !isLoading) {
      setIsLoading(true)
      setPage(prev => prev + 1);
    }
  }, [hasMore, isLoading]);

  // Trigger loadMore when page changes
  useEffect(() => {
    if (page > 1) loadReportData(true)
  }, [page])

  const columns = [
    {
      key: 'sr',
      title: 'Sr#',
      render: (text, record, index) => index + 1,
      width: '4%',
    },
    {
      key: 'item_id',
      title: 'Item#',
      render: (text, record) => (
        <span
          onClick={() => window.open(`/items#view:${record.item_id}`, '_blank')}
          style={{ color: 'blue', cursor: 'pointer' }}
        >
          {record.item_id}
        </span>
      ),
    },
    {
      key: 'grn_no',
      title: 'GRN #',
      render: (text, record) => (
        <span
          onClick={() =>
            window.open(`/goods_recevied_note#view:${record.goods_received_note?.id}`, '_blank')
          }
          style={{ color: 'blue', cursor: 'pointer' }}
        >
          {record.goods_received_note?.code}
        </span>
      ),
    },
    {
      key: 'po_no',
      title: 'PO #',
      render: (text, record) => (
        <span
          onClick={() =>
            window.open(
              `/purchase_order#view:${record.goods_received_note?.purchase_order?.id}`,
              '_blank',
            )
          }
          style={{ color: 'blue', cursor: 'pointer' }}
        >
          {record.goods_received_note?.purchase_order?.code}
        </span>
      ),
    },
    {
      key: 'vendor',
      title: 'Vendor',
      render: (_, record) => record.goods_received_note?.party?.name || '',
    },
    {
      key: 'project',
      title: 'Project',
      render: (_, record) => record.goods_received_note?.project?.name || '',
    },
    {
      key: 'item_name',
      title: 'Item Name',
      render: (_, record) => record.item?.name || '',
    },
    {
      key: 'item_brand',
      title: 'Item Brand',
      render: (_, record) => record.goods_received_note_details?.item_brand?.name || '',
    },
    {
      key: 'serial_no',
      title: 'Serial #',
      render: (_, record) => record.serial || '',
    },
    {
      key: 'barcode',
      title: 'Barcode',
      render: (_, record) => record.barcode?.barcode || '',
    },
  ]

  return (
    <div
      className="container-fluid"
      style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh' }}
    >
      <Form layout="vertical" form={form} onFinish={handleSearch}>
        <Card
          title={
            <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '22px' }}>
              Item Serial Report
            </div>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={15} sm={10} md={4}>
              <label>GRN Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => setDateRange(value)}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>

            <Col xs={15} sm={10} md={5}>
              <label>Item Name:</label>
              <WidgetItemSelector
                items={Items}
                onChange={(value) => changeValues('item_id', value)}
                displayValue={ItemName}
                reloadDropdownData={() => Base.dropdownData('items', {}, [setItems], 1)}
                placeHolder="Select Item"
                value={ItemId}
              />
            </Col>

            <Col xs={12} sm={8} md={8}>
              <Button
                style={{ marginTop: 30, marginRight: 5 }}
                type="primary"
                htmlType="submit"
                disabled={isLoading}
              >
                Search
              </Button>

              <Button
                style={{ marginTop: 30, backgroundColor: '#4BB543', color: '#fff' }}
                onClick={exportToExcel}
                disabled={isLoading}
              >
                Export
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>

      <Card style={{ marginTop: 30 }}>
        <div id="scrollableDiv" style={{ height: 400, overflowY: 'auto' }} onScroll={handleScroll}>
          <Table
            columns={columns}
            dataSource={ItemSerialReportData}
            rowKey="id"
            pagination={false}
            bordered
            loading={isLoading && page === 1}
          />

          {isLoading && page > 1 && (
            <div style={{ textAlign: 'center', padding: 10 }}>
              <Spin size="small" style={{ marginRight: 8 }} />
              Loading more...
            </div>
          )}

          {!hasMore && <div style={{ textAlign: 'center', padding: 10 }}>No more records</div>}
        </div>
      </Card>
    </div>
  )
}

export default ItemSerialReport
